import { useState, useEffect } from 'react'
import type { Seed } from '../types'

export function useSeeds() {
  const [seeds, setSeeds] = useState<Seed[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchKey, setFetchKey] = useState(0)

  useEffect(() => {
    async function fetchSeeds() {
      try {
        setLoading(true)
        const response = await fetch('/api/seeds')
        if (!response.ok) {
          throw new Error('Failed to fetch seeds')
        }
        const data = await response.json()
        setSeeds(data.seeds || [])
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    }

    fetchSeeds()
  }, [fetchKey])

  return { seeds, loading, error, refetch: () => setFetchKey(k => k + 1) }
}

export function useSeed(name: string) {
  const [seed, setSeed] = useState<Seed | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!name) { setLoading(false); return }
    setLoading(true)
    async function fetchSeed() {
      try {
        const response = await fetch(`/api/seeds/${name}`)
        if (!response.ok) {
          if (response.status === 404) {
            setSeed(null)
            return
          }
          throw new Error('Failed to fetch seed')
        }
        const data = await response.json()
        setSeed(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    }

    fetchSeed()
  }, [name])

  return { seed, loading, error }
}
